import {Button, Dialog, DialogBody, DialogFooter, Text, TextArea} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Fragment, useState} from "react";
import {AssignmentState} from "../../../../providers/AssignmentReducer";
import {DriverRequirementEnforcementLevel} from "../../../settings/enums/DriverRequirementEnforcementLevel";
import "./AssignJobsConfirmation.css";
import {useDispatchDataContext} from "../../../../providers/DispatchDataProvider";
import {getArrayFromField} from "../../../../utils/General";
import {truncateSkillList} from "../../../../utils/assignment/Assignment.utils";

const MAX_NOTE_LENGTH = 255;
const MIN_REASON_LENGTH = 50;

const DialogBox = styled(Dialog)`
  box-sizing: border-box;
  background: #fcfcfc;
  box-shadow: 0 7px 20px rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  width: 390px;
  &.prevent {
    background: #ffdee0;
    .bp5-dialog-header {
      background: #ffdee0;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  padding-left: 13px;
  padding-top: 13px;
`;

const Title = styled(Text)`
  font-family: "Roboto", serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  padding-left: 19px;
  color: #161616;
`;

const HeaderIcon = styled(FontAwesomeIcon)``;

const Body = styled(DialogBody)`
  background: transparent;
  font-family: "Roboto", serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18.51px;

  padding: 9px 28px 14px;
`;

const BodyText = styled(Text)`
  color: #161616;
  &.body-text-bottom {
    padding-right: 4px;
  }
`;

const BodyTextCenter = styled(BodyText)`
  padding: 24px 0;
`;

const TextDetail = styled.div`
  padding-bottom: 12px;
  &:last-child {
    padding-bottom: 0;
  }
`;

const JobNumberText = styled(Text)`
  color: #0000ee;
  display: inline;
`;

const RequireMentText = styled(Text)`
  color: #fa545e;
  display: inline;
  font-style: italic;
`;

const ReasonTextArea = styled(TextArea)`
  width: 100%;
  min-height: 48px;
  margin-top: 6px;
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  resize: vertical;
`;

const ReasonLabel = styled(Text)`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const Footer = styled(DialogFooter)`
  background: transparent;
  border-radius: 0 0 0 0;
  border-top: 0;
  padding: 0 28px 18px;
`;

const ConfirmButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  background: #ffffff !important;
  box-shadow: 0px 2px 2px 0px #00000040 !important;
  border-radius: 4px;
  border: none;
  outline: none;
  font-family: "Roboto", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  &.button-no {
    color: #161616;
  }
  &:hover {
    background: rgba(143, 153, 168, 0.15) !important;
  }
  &:disabled {
    :hover {
      background: #ffffff !important;
    }
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface AssignJobsConfirmationProps {
  testIdPrefix: string;
  isVisible: boolean;
  driverRequiredSetting: string;
  assignmentState: AssignmentState;
  isSkillsBasedRequirements: boolean;
  handleConfirm(reasons: Record<number, string>): void;
  handleClose(): void;
}

export const AssignJobsConfirmation = ({
  testIdPrefix,
  isVisible,
  assignmentState,
  driverRequiredSetting,
  isSkillsBasedRequirements = false,
  handleConfirm,
  handleClose
}: AssignJobsConfirmationProps) => {
  const {skills} = useDispatchDataContext();
  const isReassignAction = assignmentState.selectedStops.length > 0;
  const isWarningMode = driverRequiredSetting === DriverRequirementEnforcementLevel.Warning;
  const requiresOverrideReason = isWarningMode && isSkillsBasedRequirements;

  const [reasonsByJobId, setReasonsByJobId] = useState<Record<number, string>>({});

  const updateReason = (jobId: number, reason: string) => {
    setReasonsByJobId((prev) => ({...prev, [jobId]: reason}));
  };

  const activeJobSkillIds = skills.map((skill) => skill.id);
  const driverSkillIds = new Set((assignmentState.selectedManifest?.driver?.skills ?? []).map((s) => s.id));

  const getMissingSkillNames = (orderSkills: Array<{id: number; name: string}> | null | undefined): string[] => {
    const required = (orderSkills ?? []).filter((s) => activeJobSkillIds.includes(s.id));
    return required.filter((s) => !driverSkillIds.has(s.id)).map((s) => s.name);
  };

  const hasMissingSkills = (orderSkills: Array<{id: number}> | null | undefined): boolean => {
    const required = (orderSkills ?? []).filter((s) => activeJobSkillIds.includes(s.id));
    return required.some((s) => !driverSkillIds.has(s.id));
  };

  const getMismatchedJobIds = (): number[] => {
    if (isReassignAction) {
      return Array.from(new Map(assignmentState.selectedStops.map((item) => [item.job.jobNumber, item])).values())
        .filter((stop) =>
          isSkillsBasedRequirements
            ? hasMissingSkills(stop?.order?.orderSkills)
            : (stop?.order?.requirements ?? []).length > 0
        )
        .map((stop) => stop.job.jobId);
    }
    return assignmentState.selectedJobs
      .filter((job) =>
        isSkillsBasedRequirements
          ? hasMissingSkills(job?.order?.orderSkills)
          : (job?.order?.requirements ?? []).length > 0
      )
      .map((job) => job.jobId);
  };

  const mismatchedJobIds = getMismatchedJobIds();

  const allReasonsProvided =
    !requiresOverrideReason || mismatchedJobIds.every((jobId) => (reasonsByJobId[jobId] ?? "").trim().length > 0);

  const onConfirm = () => {
    handleConfirm(reasonsByJobId);
    setReasonsByJobId({});
  };

  const onClose = () => {
    handleClose();
    setReasonsByJobId({});
  };

  const actions = isWarningMode ? (
    <Fragment>
      <ConfirmButton
        className="button-no"
        data-testid={`${testIdPrefix}-dialog-no-button`}
        text={"No"}
        onClick={onClose}
      />
      <ConfirmButton
        data-testid={`${testIdPrefix}-dialog-yes-button`}
        text={"Yes, Assign"}
        style={{color: "#fa545e"}}
        onClick={onConfirm}
        disabled={!allReasonsProvided}
      />
    </Fragment>
  ) : (
    <ConfirmButton
      className="button-no"
      data-testid={`${testIdPrefix}-dialog-no-button`}
      text={"Close"}
      onClick={onClose}
    />
  );

  const title = isWarningMode ? (
    <Header>
      <HeaderIcon data-testid={"header-icon"} color={"#FF6B00"} size={"xl"} icon={solid("circle-exclamation")} />
      <Title data-testid={"header-title"}>{`Assign to ${assignmentState?.selectedManifest?.driver?.name}?`}</Title>
    </Header>
  ) : (
    <Header>
      <HeaderIcon data-testid={"header-icon"} color={"#FA545E"} size={"xl"} icon={solid("triangle-exclamation")} />
      <Title data-testid={"header-title"}>Can&apos;t Assign to This Driver</Title>
    </Header>
  );

  const renderJobSkillsWithReason = (
    jobId: number,
    jobNumber: string,
    requirements: JSX.Element[],
    skillNames: string[]
  ) => {
    const reason = reasonsByJobId[jobId] ?? "";
    const maxPrefixLength = MAX_NOTE_LENGTH - MIN_REASON_LENGTH;
    const skillList = skillNames.length > 0 ? truncateSkillList(skillNames, maxPrefixLength) : "";
    const prefixLength = skillList ? `Missing: ${skillList} | `.length : 0;
    const effectiveMax = MAX_NOTE_LENGTH - prefixLength;
    const isMismatched = mismatchedJobIds.includes(jobId);
    return (
      <TextDetail key={jobNumber}>
        Job <JobNumberText>{jobNumber}</JobNumberText> - {requirements}
        {requiresOverrideReason && isMismatched && (
          <>
            <ReasonLabel>
              Reason<span style={{color: "#fa545e"}}>*</span>:
            </ReasonLabel>
            <ReasonTextArea
              data-testid={`${testIdPrefix}-reason-${jobId}`}
              placeholder="Please enter the override reason"
              value={reason}
              onChange={(e) => updateReason(jobId, e.target.value)}
              maxLength={effectiveMax}
            />
          </>
        )}
      </TextDetail>
    );
  };

  const bodyJobTextCenter = assignmentState.selectedJobs
    .filter((job) =>
      isSkillsBasedRequirements
        ? hasMissingSkills(job?.order?.orderSkills)
        : (job?.order?.requirements ?? []).length > 0
    )
    .map((job) => {
      const activeOrderSkills = job?.order?.orderSkills?.filter((skill) => activeJobSkillIds.includes(skill.id));
      const orderRequirements = (
        isSkillsBasedRequirements ? getArrayFromField(activeOrderSkills) : getArrayFromField(job?.order?.requirements)
      ).map((requirement) => requirement.name);
      const requirements = orderRequirements.map((requirement, index) => {
        if (index !== orderRequirements.length - 1) {
          return (
            <>
              <RequireMentText>&apos;{requirement}&apos;</RequireMentText>;{" "}
            </>
          );
        }
        return <RequireMentText key={requirement}>&apos;{requirement}&apos;</RequireMentText>;
      });
      const missingSkillNames = getMissingSkillNames(job?.order?.orderSkills);
      return renderJobSkillsWithReason(job.jobId, job.jobNumber, requirements, missingSkillNames);
    });

  const bodyStopTextCenter = Array.from(
    new Map(
      assignmentState.selectedStops.map((item) => {
        return [item.job.jobNumber, item];
      })
    ).values()
  )
    .filter((stop) =>
      isSkillsBasedRequirements
        ? hasMissingSkills(stop?.order?.orderSkills)
        : (stop?.order?.requirements ?? []).length > 0
    )
    .map((stop) => {
      const activeOrderSkills = stop?.order?.orderSkills?.filter((skill) => activeJobSkillIds.includes(skill.id));
      const orderRequirements = (
        isSkillsBasedRequirements ? getArrayFromField(activeOrderSkills) : getArrayFromField(stop?.order?.requirements)
      ).map((requirement) => requirement.name);
      const requirements = orderRequirements.map((requirement, index) => {
        if (index !== orderRequirements.length - 1) {
          return (
            <>
              <RequireMentText>&apos;{requirement}&apos;</RequireMentText>;{" "}
            </>
          );
        }
        return <RequireMentText key={requirement}>&apos;{requirement}&apos;</RequireMentText>;
      });
      const missingSkillNames = getMissingSkillNames(stop?.order?.orderSkills);
      return renderJobSkillsWithReason(stop.job.jobId, stop.job.jobNumber, requirements, missingSkillNames);
    });

  const bodyTextBottom = isWarningMode
    ? "Would you like to proceed with assignment anyway?"
    : "If you feel this is by mistake, please navigate to 'Driver Information' within CMS to update this driver's record";

  return (
    <DialogBox
      data-testid={`${testIdPrefix}-dialog-box`}
      title={title}
      isOpen={isVisible}
      onClose={onClose}
      className={`assign-jobs-confirmation-dialog ${driverRequiredSetting.toLowerCase()}`}
    >
      <Body>
        <BodyText>
          {`The job(s) have ${
            isSkillsBasedRequirements ? "skill(s)" : "requirement(s)"
          } that the selected driver does not have based on our system's record.`}
        </BodyText>
        <BodyTextCenter>{isReassignAction ? bodyStopTextCenter : bodyJobTextCenter}</BodyTextCenter>
        <BodyText data-testid={"body-text-bottom"} className="body-text-bottom">
          {bodyTextBottom}
        </BodyText>
      </Body>
      <Footer actions={actions}></Footer>
    </DialogBox>
  );
};
